export type Identity = {
  id: string;
  provider: string;
  usernameDisplay: string | null;
  status: string;
};
export type Account = {
  id: string;
  displayName: string;
  status: string;
  recordedAt: string;
  identities: Identity[];
  activeGrantCount: number;
};
export type Grant = {
  id: string;
  accountId: string;
  roleCode: string;
  scopeMode: string;
  scopeOrganizationIds: string[];
  validFrom: string;
  validTo: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  grantedByAccountId: string | null;
  reviewStatus: "PENDING_REVIEW" | "REVIEWED" | "NOT_REQUIRED";
  reviewedByAccountId: string | null;
  reviewedAt: string | null;
  grantBasis: "STAFF_ASSIGNMENT" | "APPOINTMENT" | "DELEGATION";
  basisAppointmentId: string | null;
  delegatedFromGrantId: string | null;
  accountName: string;
  accountStatus: string;
  issuerName: string | null;
  reviewerName: string | null;
  scopeOrganizations: Array<{ id: string; name: string }>;
  appointment: { personName: string; termName: string; postLabel: string } | null;
};
export type Gap = {
  appointmentId: string;
  personId: string;
  personName: string;
  organizationId: string;
  organizationName: string;
  termId: string;
  termName: string;
  postCode: string;
  accountId: string | null;
};
export type Candidate = { id: string; displayName: string; hasActiveGrant: boolean };
export type Authority = { items: Grant[]; delegations: Grant[] };
export type GrantState = "ACTIVE" | "PENDING" | "SCHEDULED" | "EXPIRED" | "REVOKED";
export const GRANT_LABEL: Record<GrantState, string> = {
  ACTIVE: "已生效",
  PENDING: "待复核",
  SCHEDULED: "未到生效时间",
  EXPIRED: "已到期",
  REVOKED: "已撤销"
};
export const GRANT_TONE: Record<GrantState, "success" | "warning" | "neutral" | "brand"> = {
  ACTIVE: "success",
  PENDING: "warning",
  SCHEDULED: "brand",
  EXPIRED: "neutral",
  REVOKED: "neutral"
};
export const BASIS_LABEL = {
  STAFF_ASSIGNMENT: "机关分派",
  APPOINTMENT: "任职授权",
  DELEGATION: "他人转授"
};
export const REVOKE_LABEL: Record<string, string> = {
  APPOINTMENT_ENDED: "任职已结束",
  APPOINTMENT_CLOSED: "任职已收档",
  TERM_CLOSED: "届次已收档",
  UPSTREAM_GRANT_REVOKED: "上游授权已撤销"
};
export const POST_LABEL: Record<string, string> = {
  CHAIR: "主任委员",
  VICE_CHAIR: "副主任委员",
  SECRETARY_GENERAL: "秘书长",
  DEPUTY_SECRETARY_GENERAL: "副秘书长",
  BRANCH_CHAIR: "支部主任委员",
  BRANCH_VICE_CHAIR: "支部副主任委员"
};
export function grantState(g: Grant, now = Date.now()): GrantState {
  if (g.revokedAt) return "REVOKED";
  if (g.validTo && Date.parse(g.validTo) <= now) return "EXPIRED";
  if (g.reviewStatus === "PENDING_REVIEW") return "PENDING";
  if (Date.parse(g.validFrom) > now) return "SCHEDULED";
  return "ACTIVE";
}
export const scopeText = (g: Grant) =>
  g.scopeOrganizations.map((o) => o.name).join("、") +
  (g.scopeMode === "SUBTREE_DYNAMIC" ? "及下级" : "（仅所列组织）");
export const localTime = (value: Date) =>
  new Date(value.getTime() - value.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
export type AccessEditor =
  | {
      kind: "account";
      action: "create" | "enable" | "disable" | "bind" | "unbind";
      record?: Account;
      identity?: Identity;
      gap?: Gap;
    }
  | {
      kind: "grant";
      action: "issue" | "review" | "revoke" | "delegate" | "shorten";
      record?: Grant;
      gap?: Gap;
      accountId?: string;
      own?: boolean;
    }
  | { kind: "password"; action: "password" };
